import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'

import apiClient from '@disco/api/server'
import { getUser } from '@disco/state-management/selectors/user'
import captureError from '@disco/utils/captureError'

import { useSelector } from 'hooks/use-redux'

import { SchoolOfDiscoBanner } from '.'

const emptyBanner = {
  bannerNode: null,
  heightPx: 0,
}

export function useSchoolOfDiscoBanner() {
  const user = useSelector(getUser)

  const isTrial = user?.business?.private_metadata?.plan_type === 'trial'

  const dismissedAt =
    user?.settings?.school_of_disco_banner_dismissed_basic_skills_class_at

  const hasBeenTwoDaysSinceDismissed = dismissedAt
    ? dayjs(dismissedAt).add(1, 'days').isBefore(new Date(), 'day')
    : false

  const isRecentlyDismissed = dismissedAt && !hasBeenTwoDaysSinceDismissed

  const hasConfirmed =
    user?.settings?.school_of_disco_banner_confirmed_basic_skills_class

  const shouldFetch = isTrial && !isRecentlyDismissed && !hasConfirmed

  const { nextSessionDate, nextSessionUrl } = useCalendlyApi(shouldFetch)

  if (!shouldFetch || !nextSessionDate || !nextSessionUrl) {
    return emptyBanner
  }

  return {
    bannerNode: (
      <SchoolOfDiscoBanner
        nextSessionDate={nextSessionDate}
        nextSessionUrl={nextSessionUrl}
      />
    ),
    heightPx: 52,
  }
}

function useCalendlyApi(enabled: boolean) {
  const [data, setData] = useState<{
    nextSessionDate: Date | null
    nextSessionUrl: string | null
  }>({
    nextSessionDate: null,
    nextSessionUrl: null,
  })

  useEffect(() => {
    if (enabled) {
      fetchCalendlyData().then(setData)
    }
  }, [enabled])

  return data
}

async function fetchCalendlyData() {
  try {
    const data = await apiClient.getCalendlyAvailableTimes()

    return {
      nextSessionDate: new Date(data.nextSessionDate),
      nextSessionUrl: data.nextSessionUrl,
    }
  } catch (error) {
    captureError(error, { moduleName: 'SchoolOfDiscoBanner' })
    return {
      nextSessionDate: null,
      nextSessionUrl: null,
    }
  }
}
